import {
  AIBadgeScore,
  Button,
  Divider,
  FLagWithText,
  Flex,
  Group,
  Icon,
  Skeleton,
  Tag,
  Text,
  Title,
} from "components/common"
import { IconText, TitleDescription } from "components/custom"
import { configs } from "configs"
import CallButton from "features/clients/components/call-button"
import GrandStreamCallButton from "features/clients/components/grandStreamCallButton"
import WhatsappCallButton from "features/clients/components/whatsapp-call-button"
import { classifications as leadClassifications } from "features/shared/data"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { capitalizeFirstLetter, convertToReadableString, joinBytItems, localisedValue, openMailTo } from "utils"
import { fireEvent } from "utils/gtm/gtmEventHandler"
import { ACTION_TYPES, PAGE_SUBSECTIONS } from "utils/gtm/gtmEventsData"
import getPrivilege from "utils/privileges/privileges"
import theme from "utils/themeVars"
import styles from "./ClientDetailsHeader.module.less"
import { CLIENT_DETAIL_PATH } from "constants/app-paths"
import { Link } from "react-router-dom"
import { TaskOverviewModal } from "features/shared/components"
import { useRef } from "react"

const ClientDetailsHeader = ({
  id,
  idLabel,
  title,
  tag,
  classification,
  aiScore,
  descriptionItems,
  showCallBanner,
  onCallBannerClose,
  showActionButtons,
  willCallButtonTriggerSalesPlaybook,
  email,
  loading,
  client,
}) => {
  const { t } = useTranslation()
  const userPrivileges = useSelector(state => state?.app?.privileges)
  const taskTableRef = useRef()

  const getLocalizedText = (data, key = "name") => {
    if (!data) return null
    if (typeof data === "string") return data
    return localisedValue({ object: data, key })
  }

  const formatDescriptionItem = item => {
    if (!item) return null
    if (typeof item === "string") return item
    if (typeof item !== "object") return null

    const { value, object, key = "name", type, prefix } = item
    const rawValue = object ? getLocalizedText(object, key) : value
    if (!rawValue) return null

    let formatted = rawValue
    if (type === "capitalize") formatted = capitalizeFirstLetter(rawValue)
    else if (type === "readable") formatted = convertToReadableString(rawValue)
    else if (type === "city")
      formatted = <FLagWithText title={object?.countryName} infoText={rawValue} countryCode={object?.loc} />

    return <Text type="secondary">{prefix ? `${prefix} ${formatted}` : formatted}</Text>
  }

  const formattedId = id != null ? `${idLabel || t("ID")}: ${id}` : null
  const titleText = getLocalizedText(title)
  const tagText = getLocalizedText(tag)
  const classificationColor =
    leadClassifications(true)[classification?.key]?.style?.color || classification?.style?.color
  const processedDescriptionItems = descriptionItems?.map(formatDescriptionItem).filter(Boolean) || []
  const hasIdRow = formattedId || tagText
  const hasTitleRow = titleText || classification?.name || aiScore != null
  const hasDescription = processedDescriptionItems.length > 0

  if (loading) {
    return (
      <Group template="initial" gap="24px">
        <Group gap="14px">
          <Flex align="center" gap="8px">
            <Skeleton height={18} width={60} />
            <Skeleton height={18} width={60} />
          </Flex>

          <div>
            <Skeleton height={30} width={200} block />
            <Skeleton height={15} width={200} />
          </div>

          <Skeleton type="button" width={150} />
        </Group>
      </Group>
    )
  }

  return (
    <>
      <Group template="initial" gap="24px">
        <Group gap="14px">
          {hasIdRow && (
            <Flex align="center" className={styles.idRow}>
              {formattedId && (
                <Link className="text-link fs14" target="_blank" to={CLIENT_DETAIL_PATH.replace(":clientId", id)}>
                  {formattedId}
                </Link>
              )}
              {tagText && (
                <Tag className="fs12" color={tag?.style?.bgColor || tag?.color}>
                  {tagText}
                </Tag>
              )}
            </Flex>
          )}

          {hasTitleRow && (
            <div>
              <TitleDescription
                title={
                  <Flex align="center" gap="8px" className="mbe-4">
                    {titleText && (
                      <Title level={3} className="mbe-0">
                        {titleText}
                      </Title>
                    )}
                    {classification?.name && (
                      <IconText
                        icon={classification.icon || "MdLocalFireDepartment"}
                        message={classification.name}
                        iconProps={{
                          color: classificationColor,
                          hasBackground: true,
                          iconContainerStyle: { padding: 6 },
                        }}
                        title=""
                        gap={"0px"}
                      />
                    )}
                    {aiScore != null && (
                      <AIBadgeScore
                        totalScore={`${aiScore}/100`}
                        icon={aiScore < 50 ? "FaArrowDown" : "FaArrowUp"}
                        buttonProps={{
                          style: { height: 30 },
                          size: "small",
                          shape: "round",
                        }}
                        handleDrawer={() => taskTableRef.current?.setVisible(true)}
                      />
                    )}
                  </Flex>
                }
                description={
                  hasDescription && (
                    <Flex align="center" gap="6px" className="fw-normal">
                      {joinBytItems(processedDescriptionItems, <Icon icon="GoDotFill" size="8px" color="#D6D6D6" />)}
                    </Flex>
                  )
                }
                gapSpace={1}
              />
            </div>
          )}

          {showCallBanner && (
            <div className={styles.callInitiatedBanner}>
              <Flex align="center" justify="space-between" className={styles.callBannerContent}>
                <Text style={{ "--typography-color": "#fff" }} className={"fw-medium"}>
                  {t("Call Initiated")} ...
                </Text>
                <Divider type="vertical" className={styles.callBannerDivider} />
                <Button
                  type="text"
                  icon="RxCross2"
                  iconProps={{ size: "14px", color: "#fff" }}
                  onClick={e => {
                    e.stopPropagation()
                    onCallBannerClose?.()
                  }}
                  className={styles.callBannerCloseBtn}
                />
              </Flex>
            </div>
          )}

          {!showCallBanner && showActionButtons && (
            <Flex gap="6px">
              <Button
                icon="HiOutlineMail"
                type="light"
                color={theme["primary-color"]}
                text={t("Email")}
                shape="round"
                style={{ padding: "16px" }}
                disabled={!getPrivilege("can_send_email", userPrivileges)}
                onClick={() => {
                  fireEvent({
                    ...ACTION_TYPES.email_contact_client,
                    user_input: email,
                    client_id: id,
                  })
                  openMailTo(email)
                }}
              />
              <WhatsappCallButton
                client={{ id: id }}
                from={{ interacted_from: PAGE_SUBSECTIONS.LEADS_PATH }}
                disabled={!getPrivilege("can_view_client_contact", userPrivileges)}
                text={t("Whatsapp")}
                style={{ padding: "16px" }}
                shape="round"
              />
              <CallButton
                client={{ id: id }}
                from={{ interacted_from: PAGE_SUBSECTIONS.LEADS_PATH }}
                className={styles.callBtnTEXT}
                disabled={!getPrivilege("can_view_client_contact", userPrivileges)}
                text={t("Call")}
                style={{ padding: "16px" }}
                shape="round"
                willTriggerSalesPlaybook={willCallButtonTriggerSalesPlaybook}
              />
              {configs?.allowGrandStreamCall && (
                <GrandStreamCallButton
                  client={{ id: id }}
                  from={{
                    page: PAGE_SUBSECTIONS.LEADS_PATH,
                    interacted_from: PAGE_SUBSECTIONS.LEADS_PATH,
                  }}
                  className={styles.callBtnTEXT}
                  disabled={!getPrivilege("can_view_client_contact", userPrivileges)}
                  text={t("Grandstream")}
                  style={{ padding: "16px" }}
                  shape="round"
                  willTriggerSalesPlaybook={willCallButtonTriggerSalesPlaybook}
                />
              )}
            </Flex>
          )}
        </Group>
      </Group>
      <TaskOverviewModal client={client} tabKey="conversionProbability" ref={taskTableRef} />
    </>
  )
}

ClientDetailsHeader.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  idLabel: PropTypes.string,
  title: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  tag: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  classification: PropTypes.shape({
    name: PropTypes.string,
    key: PropTypes.string,
    icon: PropTypes.string,
    style: PropTypes.shape({ color: PropTypes.string }),
  }),
  aiScore: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  descriptionItems: PropTypes.array,
  showCallBanner: PropTypes.bool,
  onCallBannerClose: PropTypes.func,
  showActionButtons: PropTypes.bool,
  willCallButtonTriggerSalesPlaybook: PropTypes.bool,
  email: PropTypes.string,
  phone: PropTypes.string,
  onEmailClick: PropTypes.func,
  onWhatsAppClick: PropTypes.func,
  onCallClick: PropTypes.func,
  loading: PropTypes.bool,
}

ClientDetailsHeader.defaultProps = {
  id: null,
  idLabel: null,
  title: null,
  tag: null,
  classification: null,
  aiScore: null,
  descriptionItems: [],
  showCallBanner: false,
  onCallBannerClose: () => {},
  showActionButtons: true,
  willCallButtonTriggerSalesPlaybook: true,
  email: null,
  phone: null,
  onEmailClick: () => {},
  onWhatsAppClick: () => {},
  onCallClick: () => {},
  loading: false,
}

export default ClientDetailsHeader
